import React, { ReactNode, useMemo } from 'react';

import PropTypes from 'prop-types';

import AnimateHeight from '@adsk/alloy-react-animate-height';
import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
  StyleProp,
} from '@adsk/alloy-react-theme';

const getChevronAnimation = ({ expanded }: { expanded: boolean }) => {
  const transform = expanded ? 'rotate(0deg)' : 'rotate(-90deg)';

  return {
    transform,
    transition: 'transform 0.15s linear',
  };
};

type AccordionItemProps = Omit<StylableComponent<HTMLDivElement>, 'title'> & {
  expanded?: boolean;
  label?: ReactNode;
  onExpand?: (event: React.MouseEvent<HTMLElement>) => void;
  rightAligned?: boolean;
  title?: ReactNode | string;
  animationTimeout?: number;
  onAnimationComplete?: () => void;
  headerClassName?: string;
  headerStyle?: StyleProp;
  buttonProps?: {
    id?: string;
    'aria-controls'?: string;
  };
  containerProps?: {
    id?: string;
    role?: string;
    'aria-labelledby'?: string;
  };
};

/**
 * An expandable panel component which can be used in an accordion.
 */
const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  (
    {
      style,
      className,
      headerStyle,
      headerClassName,
      children,
      expanded = false,
      label,
      onExpand,
      rightAligned = false,
      title,
      animationTimeout,
      buttonProps,
      containerProps,
      onAnimationComplete,
      ...props
    },
    ref,
  ) => {
    const chevronAnimation = useMemo(
      () => getChevronAnimation({ expanded }),
      [expanded],
    );

    return (
      <div
        className={className}
        css={[
          {
            margin: `${theme.spacing.XXXS}px 0`,
            width: '100%',
            ':focus': {
              boxShadow: theme.halos.focus,
            },
          },
          style,
        ]}
        ref={ref}
        role="heading"
        aria-level={1}
        {...props}
      >
        <button
          className={headerClassName}
          aria-expanded={expanded}
          css={[
            {
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              backgroundColor: theme.tokens.colors.background.secondary.value,
              color: theme.tokens.colors.text.secondary.value,
              cursor: 'pointer',
              textAlign: 'left',
              outline: 'none',
              border: 'none',
              ...(rightAligned && {
                flexDirection: 'row-reverse',
              }),
              ...theme.typography.labelSmall,
              minHeight: '32px',
              position: 'relative',
              ...(expanded && {
                color: theme.tokens.colors.text.primary.value,
              }),
              '&:hover, &:focus': {
                color: theme.tokens.colors.text.primary.value,
              },
            },
            headerStyle,
          ]}
          onClick={onExpand}
          {...buttonProps}
        >
          <ChevronDownIcon
            size={16}
            style={{
              marginLeft: theme.spacing.XS,
              marginRight: rightAligned ? theme.spacing.M : 0,
              ...chevronAnimation,
            }}
          />
          <div
            css={[
              {
                flex: 'auto',
                marginBottom: theme.spacing.XXS,
                marginLeft: rightAligned ? theme.spacing.L : theme.spacing.XXS,
                marginRight: theme.spacing.M,
                marginTop: theme.spacing.XXS,
              },
            ]}
          >
            {title}
          </div>
          {!rightAligned && (
            <div
              css={[
                {
                  color: theme.tokens.colors.text.secondary.value,
                  flex: '0 0 auto',
                  marginRight: theme.spacing.M,
                },
              ]}
            >
              {label}
            </div>
          )}
        </button>
        <AnimateHeight
          isOpen={!!expanded}
          timeout={animationTimeout}
          onAnimationComplete={onAnimationComplete}
          {...containerProps}
        >
          {children}
        </AnimateHeight>
      </div>
    );
  },
);

AccordionItem.displayName = 'AccordionItem';

AccordionItem.propTypes = {
  /** Styles applied to the root(Content) element */
  style: stylePropType,
  /** Class applied to the root(Content) element */
  className: PropTypes.string,
  /** Styles applied to the button(Header) element */
  headerStyle: stylePropType,
  /** Class applied to the button(Header) element */
  headerClassName: PropTypes.string,
  /** Items to be displayed when the panel is expanded */
  children: PropTypes.any,
  /** Whether or not the panel is expanded */
  expanded: PropTypes.bool,
  /** The secondary label to display on the right of the title bar */
  label: PropTypes.any,
  /** Callback method for when the panel is expanded or contracted */
  onExpand: PropTypes.func,
  /** Whether or not the chevron icon is right-aligned. If true, label will be
   *  hidden */
  rightAligned: PropTypes.bool,
  /** The main title to display on the left of the title bar */
  title: PropTypes.any,
  /** Height animation timeout */
  animationTimeout: PropTypes.number,
  /** Callback when animation is complete */
  onAnimationComplete: PropTypes.func,
};

export default AccordionItem;
